// components/notifications/notification/person-notification.components.jsx
import React from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  BsBellFill,
  BsBookmarkCheckFill,
  BsFillChatSquareQuoteFill,
  BsFillChatSquareTextFill,
  BsFillPersonCheckFill,
  BsFillTelephoneForwardFill,
} from "react-icons/bs";
import { IoChatbubblesSharp } from "react-icons/io5";
import { personNotfUpdateSeen } from "../../../firebase/firebase.utils";
import "./person-notification.styles.scss";

const notificationIcons = {
  assign: BsFillPersonCheckFill,
  "task starting": BsBookmarkCheckFill,
  comment: BsFillChatSquareQuoteFill,
  msgprivate: BsFillChatSquareTextFill,
  "workspace message": IoChatbubblesSharp,
  "project message": IoChatbubblesSharp,
  privatecall: BsFillTelephoneForwardFill,
  "incoming call": BsFillTelephoneForwardFill,
};
const CHAT_SEARCH_PARAMS = ["dmchat", "wschat", "prchat", "fromws"];

function getInternalPath(clickAction) {
  if (!clickAction) return "";

  try {
    const url = new URL(String(clickAction), window.location.origin);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return "";
  }
}
function getParamFromClickAction(clickAction, paramName) {
  if (!clickAction) return "";
  try {
    return (
      new URL(String(clickAction), window.location.origin).searchParams.get(
        paramName,
      ) || ""
    );
  } catch {
    return "";
  }
}
function getDirectValueOrUrlParam(clickAction, paramName) {
  const valueFromUrl = getParamFromClickAction(clickAction, paramName);
  if (valueFromUrl) return valueFromUrl;
  const directValue = String(clickAction || "");
  // A room ID is not a URL path or query string.
  return directValue && !/[/?#]/.test(directValue) ? directValue : "";
}

// Used by both the bell row and the incoming-call banner.
export function getNotificationCallType(notification) {
  if (["private", "workspace", "project"].includes(notification?.callType))
    return notification.callType;
  const type = notification?.type;
  if (type === "callprivate" || type === "privatecall") return "private";
  if (type === "callworkspace") return "workspace";
  if (type === "callproject") return "project";
  if (type === "incoming call" || type === "groupcall") {
    if (
      notification?.stationId ||
      getParamFromClickAction(notification?.click_action, "prchat")
    )
      return "project";
    if (
      notification?.spaceId ||
      getParamFromClickAction(notification?.click_action, "wschat")
    )
      return "workspace";
    return "private";
  }
  return null;
}

export function getCallChatParams(notification) {
  const callType = getNotificationCallType(notification);
  const action = notification?.click_action;
  if (callType === "private") {
    const dmchat =
      notification?.conversationRef ||
      getDirectValueOrUrlParam(action, "dmchat");
    return dmchat ? { dmchat } : null;
  }
  if (callType === "workspace") {
    const wschat =
      notification?.spaceId || getDirectValueOrUrlParam(action, "wschat");
    return wschat ? { wschat } : null;
  }
  if (callType === "project") {
    const prchat =
      notification?.stationId || getDirectValueOrUrlParam(action, "prchat");
    const fromws =
      notification?.spaceId || getParamFromClickAction(action, "fromws");
    return prchat && fromws ? { prchat, fromws } : null;
  }
  return null;
}

function PersonNotification({ notification, userId, handleClose }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const type = notification?.type;
  const clickAction = notification?.click_action;
  const callType = getNotificationCallType(notification);
  const NotificationIcon = callType
    ? BsFillTelephoneForwardFill
    : notificationIcons[type] || BsBellFill;
  const iconColor = notification?.seen ? "lightgray" : undefined;

  const setChatSearchParams = (chatParams) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    CHAT_SEARCH_PARAMS.forEach((paramName) =>
      nextSearchParams.delete(paramName),
    );
    Object.entries(chatParams).forEach(([paramName, value]) => {
      if (value) nextSearchParams.set(paramName, value);
    });
    setSearchParams(nextSearchParams);
  };
  const openPrivateChat = () => {
    const privateChatId =
      notification?.conversationRef ||
      getDirectValueOrUrlParam(clickAction, "dmchat");
    if (!privateChatId) {
      console.warn(
        "Private message notification has no DM target:",
        notification,
      );
      return;
    }
    setChatSearchParams({ dmchat: privateChatId });
  };
  const openWorkspaceChat = () => {
    const workspaceId =
      notification?.spaceId || getParamFromClickAction(clickAction, "wschat");
    if (!workspaceId) {
      console.warn("Workspace notification has no spaceId:", notification);
      return;
    }
    setChatSearchParams({ wschat: workspaceId });
  };
  const openProjectChat = () => {
    const projectId =
      notification?.stationId || getParamFromClickAction(clickAction, "prchat");
    const workspaceId =
      notification?.spaceId || getParamFromClickAction(clickAction, "fromws");
    if (!projectId || !workspaceId) {
      console.warn(
        "Project notification is missing stationId or spaceId:",
        notification,
      );
      return;
    }
    setChatSearchParams({ prchat: projectId, fromws: workspaceId });
  };
  const handleNotificationClick = () => {
    if (callType) {
      const chatParams = getCallChatParams(notification);
      if (!chatParams) {
        console.warn(
          "Call notification has no valid chat target:",
          notification,
        );
        return;
      }
      setChatSearchParams(chatParams);
    } else if (type === "task starting" && notification?.taskId) {
      const basePath = location.pathname
        .replace(/\/task\/[^/]+\/?$/, "")
        .replace(/\/$/, "");
      navigate(`${basePath}/task/${notification.taskId}`);
    } else if (type === "msgprivate" || type === "privatecall") {
      openPrivateChat();
    } else if (type === "workspace message") {
      openWorkspaceChat();
    } else if (type === "project message") {
      openProjectChat();
    } else if (
      /invit/i.test(type || "") ||
      notification?.inviteId ||
      getParamFromClickAction(clickAction, "inviteId") ||
      getParamFromClickAction(clickAction, "invites") === "1"
    ) {
      const inviteId =
        notification?.inviteId ||
        getParamFromClickAction(clickAction, "inviteId");

      navigate(
        inviteId
          ? `/invites?inviteId=${encodeURIComponent(inviteId)}`
          : "/invites",
      );
    } else {
      const target = getInternalPath(clickAction);
      if (target) navigate(target);
    }
    if (userId && notification?.id)
      personNotfUpdateSeen(userId, notification.id);
    handleClose();
  };
  return (
    <button
      type="button"
      className={`person__notification ${notification?.seen ? "person__notification--seen" : ""}`}
      onClick={handleNotificationClick}
      aria-label={`${notification?.title || "Notification"}: ${notification?.body || ""}`}
    >
      <NotificationIcon color={iconColor} size="1.4em" />
      <span className="person__notification-msg">
        <span>
          <b>{notification?.title}</b>
          <br />
          {notification?.body}
        </span>
      </span>
    </button>
  );
}
export default PersonNotification;
